// Grids & Gantts — what a column puts in a cell.
//
// One function, `cellOf(column, row, ctx)`, answers for every row kind the
// structure draws: a folder, a group header, and a work item. Keeping it
// here (and pure) means the grid renders and the Excel export writes the
// SAME value, and the rules can be tested under node without a browser.
//
// The rule that matters, and the one a structure gets right:
//
//   an aggregate belongs to a BRANCH, an own value belongs to a ROW.
//
// So Σ Estimated Time sums every distinct work item at and below the row —
// on a folder, a group and an item alike — while ZAR Value (Excl VAT),
// Assigned Resource and Client Name show what that one work item holds and
// nothing else. A group header has no work item, so it shows a blank for
// every own-value column, exactly as the structure does.
//
// SUMS ARE OVER DISTINCT WORK ITEMS, never over rows. An item named by two
// parents appears twice in the tree; adding its hours twice would invent
// work that nobody did.

import { dueInFor, formatDate } from './dueIn';
import { compileFormula, formulaHelpers } from './grouping';

export const isDone = (it) => {
  const cat = it && it.fields && it.fields.status && it.fields.status.statusCategory;
  if (!cat) return false;
  const key = String(cat.key || '').toLowerCase();
  if (key) return key === 'done';
  return String(cat.name || '').toLowerCase() === 'done';
};

export const statusOf = (it) => (it && it.fields && it.fields.status && it.fields.status.name) || '';
export const typeOf = (it) => (it && it.fields && it.fields.issuetype && it.fields.issuetype.name) || '';

export const numOf = (it, fieldId) => {
  if (!fieldId) return null;
  const v = it && it.fields ? it.fields[fieldId] : null;
  const n = typeof v === 'number' ? v : (v != null && v !== '' ? Number(v) : NaN);
  return Number.isFinite(n) ? n : null;
};

// STRUCTURAL PSEUDO-FIELDS — things a structure groups and columns by that
// are not fields on the work item at all.
//
// The one that matters here is the SPACE CATEGORY. The client's structure
// groups a folder by it, and it is easy to go looking for a custom field
// called "Space category", find one called "Category", wire that up and
// ship a column of blanks — which is exactly what happened on the first
// live run. It is not a work-item field: it belongs to the PROJECT the item
// sits in, and rides along on `fields.project.projectCategory`.
//
// Named here so a config can ask for it the way the structure names it, and
// so the admin's field list is not the only place to look.
export const PSEUDO_FIELDS = [
  { id: 'projectCategory', names: ['space category', 'project category', 'projectcategory'], label: 'Space category' },
  { id: 'projectName', names: ['project name', 'space name'], label: 'Project name' },
  { id: 'project', names: ['project', 'space', 'project key'], label: 'Project' },
];

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();

// The pseudo-field a name asks for, or null. Checked BEFORE the site's
// field list, so a site that also happens to have a custom field called
// "Project category" does not quietly win the name.
export function resolvePseudo(name) {
  const n = norm(name);
  if (!n) return null;
  const hit = PSEUDO_FIELDS.find((p) => p.id === name || p.names.includes(n));
  return hit ? hit.id : null;
}

// The display strings a field holds: a select, a multi-select, a user, a
// project, a status, a plain value.
export function textsOf(it, fieldId) {
  if (!fieldId || !it) return [];
  if (fieldId === 'key') return it.key ? [it.key] : [];
  if (fieldId === 'projectCategory') {
    const c = it.fields && it.fields.project && it.fields.project.projectCategory;
    const n = c && c.name;
    return n ? [String(n)] : [];
  }
  if (fieldId === 'projectName') {
    const n = it.fields && it.fields.project && it.fields.project.name;
    return n ? [String(n)] : [];
  }
  if (fieldId === 'project') {
    const k = (it.fields && it.fields.project && it.fields.project.key) || String(it.key || '').split('-')[0];
    return k ? [k] : [];
  }
  if (fieldId === 'status') { const s = statusOf(it); return s ? [s] : []; }
  if (fieldId === 'issuetype') { const t = typeOf(it); return t ? [t] : []; }
  const v = it.fields ? it.fields[fieldId] : null;
  if (v == null || v === '') return [];
  const one = (x) => (x == null ? '' : (typeof x === 'object' ? (x.displayName || x.value || x.name || x.key || '') : String(x)));
  return (Array.isArray(v) ? v : [v]).map(one).filter(Boolean);
}

// A number, printed the way the structure prints it: no trailing ".0", a
// thousands separator, and a blank rather than a nought when there is
// nothing there at all.
export function fmtNum(n, { blankZero = true } = {}) {
  if (n == null || !Number.isFinite(n)) return '';
  if (blankZero && n === 0) return '';
  const r = Math.round(n * 1000) / 1000;
  return r.toLocaleString('en-US', { maximumFractionDigits: 3 });
}

const DATE_FIELD = (col, ctx) => (col.field === 'duedate' ? 'duedate' : (ctx.fieldIds || {})[col.id] || '');

// The span of a date field over a branch: earliest and latest.
function spanOf(keys, fieldId, itemOf) {
  let lo = ''; let hi = '';
  keys.forEach((k) => {
    const it = itemOf(k);
    const v = it && it.fields ? String(it.fields[fieldId] || '').slice(0, 10) : '';
    if (!v) return;
    if (!lo || v < lo) lo = v;
    if (!hi || v > hi) hi = v;
  });
  return { lo, hi };
}

export function sumOver(keys, fieldId, itemOf, keep = null) {
  if (!fieldId) return null;
  let total = 0; let saw = false;
  keys.forEach((k) => {
    const it = itemOf(k);
    if (keep && (!it || !keep(it))) return;
    const n = numOf(it, fieldId);
    if (n != null) { total += n; saw = true; }
  });
  return saw ? total : null;
}

// ---- column options: which work items, which condition, how it reads --------
// All optional, all generic — a column names WORK TYPES and CONDITIONS, never
// a client's statuses or projects.
//
//   types / notTypes  the work types a sum, a count or a ratio looks at
//                     (empty = every type)
//   where / of        a condition (WHERE below): what a "Count where" counts,
//                     and a ratio's numerator (where) over denominator (of)
//   a / b             a "Difference" column: column a minus column b
//   format            { prefix, decimals, thousands } — "R 2 162 197.40"
//   bands             [{ below, color }] — the first band whose `below` the
//                     value is under paints it; `below: null` is "the rest"
//   bandAs            'chip' (a soft pill) or 'text' (coloured figures)
export const WHERE = [
  { id: 'all', label: 'Every work item' },
  { id: 'open', label: 'Not Done' },
  { id: 'done', label: 'Done' },
  { id: 'overdue', label: 'Not Done, due before today' },
  { id: 'undated', label: 'Not Done, no due date' },
];
export const BAND_COLORS = ['green', 'blue', 'amber', 'orange', 'red', 'purple', 'teal', 'grey'];

const lc = (s) => String(s || '').trim().toLowerCase();
// The work-type test a column carries, or null when it looks at every type.
export function typeTest(col) {
  const inc = (col.types || []).map(lc).filter(Boolean);
  const exc = (col.notTypes || []).map(lc).filter(Boolean);
  if (!inc.length && !exc.length) return null;
  return (it) => { const t = lc(typeOf(it)); return (!inc.length || inc.includes(t)) && !exc.includes(t); };
}
export function whereTest(where, todayIso) {
  const due = (it) => String((it.fields && it.fields.duedate) || '').slice(0, 10);
  switch (where) {
    case 'open': return (it) => !isDone(it);
    case 'done': return (it) => isDone(it);
    case 'overdue': return (it) => !isDone(it) && !!due(it) && due(it) < todayIso;
    case 'undated': return (it) => !isDone(it) && !due(it);
    default: return () => true;
  }
}
function countOver(keys, itemOf, tests) {
  let n = 0;
  keys.forEach((k) => { const it = itemOf(k); if (it && tests.every((t) => !t || t(it))) n += 1; });
  return n;
}

// A figure printed the way the column asks: a prefix after which the sign
// goes ("R -1 200.00"), a fixed number of decimals, a thousands separator.
// No format = the structure's own plain number.
export function fmtCol(n, col, opts) {
  const f = col && col.format;
  if (!f) return fmtNum(n, opts);
  if (n == null || !Number.isFinite(n)) return '';
  const dec = f.decimals == null ? null : f.decimals;
  const abs = Math.abs(n);
  let [int, frac] = (dec == null ? String(Math.round(abs * 1000) / 1000) : abs.toFixed(dec)).split('.');
  int = int.replace(/\B(?=(\d{3})+(?!\d))/g, f.thousands == null ? ',' : f.thousands);
  return `${f.prefix || ''}${n < 0 ? '-' : ''}${int}${frac ? `.${frac}` : ''}`;
}

// The band a value falls in, or ''.
export function bandOf(n, col) {
  const bands = col && col.bands;
  if (!bands || !bands.length || n == null || !Number.isFinite(n)) return '';
  const hit = bands.find((b) => b.below == null || n < b.below);
  return hit ? hit.color : '';
}

// ---- the one answer ----------------------------------------------------------
// Returns { text, num, tone, editable, fieldId } — `tone` is a hint the grid
// paints with ('late', 'done', 'muted'), never a colour, so light and dark
// stay the renderer's business.
export function cellOf(col, row, ctx = {}) {
  const base = baseCellOf(col, row, ctx);
  // A read-only column (config.js normalizeColumn) says so on every cell.
  const cell = col.readOnly && base.editable ? { ...base, editable: false } : base;
  // A coloured band is the column's, whatever the kind.
  if (col.bands && col.bands.length && cell.num != null) {
    const band = bandOf(cell.num, col);
    if (band) return { ...cell, band };
  }
  return cell;
}

// ---- formula columns -----------------------------------------------------------
// The grouping formula's language (./grouping.js), compiled once per text and
// kept: a grid of thirty thousand rows asks for the same few formulas over
// and over.
const compiled = new Map();
export function compiledFormula(src) {
  const k = String(src || '');
  if (!compiled.has(k)) { if (compiled.size > 200) compiled.clear(); compiled.set(k, compileFormula(k)); }
  return compiled.get(k);
}
// One work item's answer: { value } or { error }.
export function formulaValue(col, it, ctx) {
  const c = compiledFormula(col.formula);
  if (!c.fn) return { error: c.error || 'no formula' };
  try { return { value: c.fn(formulaHelpers(it, { resolve: ctx.resolve, todayIso: ctx.todayIso })) }; }
  catch (e) { return { error: (e && e.message) || String(e) }; }
}
const asNumber = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
// Whether a formula's answer FLAGS the work item, for a Count column.
export const flagged = (v) => !(v == null || v === '' || v === false || v === 0 || (typeof v === 'number' && !Number.isFinite(v)));
// What a formula's answer reads as in a cell.
function formulaCell(v, col) {
  if (v == null || v === '' || v === false) return { text: '' };
  if (v === true) return { text: '✓', num: 1 };
  if (typeof v === 'number') return Number.isFinite(v) ? { text: fmtCol(v, col, { blankZero: false }), num: v } : { text: '' };
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? { text: '' } : { text: formatDate(v.toISOString().slice(0, 10)), raw: v.toISOString().slice(0, 10) };
  if (typeof v === 'object') return { text: String(v.label != null ? v.label : (v.key != null ? v.key : '')) };
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? { text: formatDate(s), raw: s } : { text: s };
}

const pad2 = (n) => String(n).padStart(2, '0');
const isoDay = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

function baseCellOf(col, row, ctxIn) {
  const now = ctxIn.now || new Date();
  // TODAY, always there: "late" dates, "overdue" counts and formulas compare
  // against it, and a page that does not hand one in must not compare
  // against undefined (which is never later than anything).
  const ctx = ctxIn.todayIso ? ctxIn : { ...ctxIn, todayIso: isoDay(now) };
  const itemOf = ctx.itemOf || (() => null);
  const fieldId = DATE_FIELD(col, ctx) || (ctx.fieldIds || {})[col.id] || '';
  const it = row.it || null;
  // A LINE ROW (a line of a table in a work item's field — see ./lines.js) is
  // not a work item: it has no key, no status, no dates of its own and
  // nothing to edit, but its FIELDS read like a work item's, so a text, a
  // number, an External database or a formula column shows its value.
  const line = row.line || null;
  const rec = it || line;
  const branch = row.countKeys || new Set();

  switch (col.kind) {
    case 'key':
      return { text: it ? row.key : '', link: !!it };

    case 'summary':
      return { text: row.summary != null ? row.summary : ((it && it.fields && it.fields.summary) || ''), editable: !!it };

    case 'status':
      return { text: it ? statusOf(it) : '', tone: it && isDone(it) ? 'done' : '' };

    case 'type':
      return line ? { text: typeOf(line), tone: 'muted' } : { text: it ? typeOf(it) : '' };

    case 'date': {
      if (it) {
        const raw = it.fields ? it.fields[fieldId] : null;
        const text = formatDate(raw);
        const late = fieldId === 'duedate' && !isDone(it) && raw && String(raw).slice(0, 10) < ctx.todayIso;
        return { text, raw, editable: true, fieldId, tone: late ? 'late' : '' };
      }
      if (col.agg === 'span') {
        const { lo, hi } = spanOf(branch, fieldId, itemOf);
        return { text: lo || hi ? `${formatDate(lo)}${hi && hi !== lo ? ` → ${formatDate(hi)}` : ''}` : '', tone: 'muted' };
      }
      return { text: '' };
    }

    case 'dueIn': {
      if (!it) return { text: '' };
      const v = dueInFor(it, it.fields ? it.fields[fieldId || 'duedate'] : null, now);
      if (!v) return { text: '' };
      return { text: v.label, tone: v.overdue ? 'late' : '' };
    }

    case 'number': {
      if (line) { const n = numOf(line, fieldId); return { text: fmtCol(n, col), num: n }; }
      if (col.agg === 'sum') {
        const n = sumOver(branch, fieldId, itemOf, typeTest(col));
        // `fieldId` rides along even on a total, because a Σ column on a WORK
        // ITEM is editable: the cell shows the branch's total and the editor
        // writes that item's own value. Without the field there is nothing to
        // write to, which is why Σ columns used to refuse the edit.
        return { text: fmtCol(n, col), num: n, fieldId, editable: !!it, tone: row.isGroup ? 'agg' : '' };
      }
      if (!it) return { text: '' };
      const n = numOf(it, fieldId);
      return { text: fmtCol(n, col), num: n, editable: true, fieldId };
    }

    case 'countIf': {
      const n = countOver(branch, itemOf, [typeTest(col), whereTest(col.where, ctx.todayIso)]);
      return { text: n ? String(n) : '', num: n };
    }

    case 'ratio': {
      const t = typeTest(col);
      const den = countOver(branch, itemOf, [t, whereTest(col.of || 'all', ctx.todayIso)]);
      if (!den) return { text: '' };
      const num = countOver(branch, itemOf, [t, whereTest(col.of || 'all', ctx.todayIso), whereTest(col.where, ctx.todayIso)]);
      const pct = Math.round((num / den) * 1000) / 10;
      return { text: `${Math.round(pct)}%`, num: pct, pct, done: num, total: den };
    }

    case 'diff': {
      const cols = ctx.columns || [];
      const A = cols.find((c) => c.id === col.a); const B = cols.find((c) => c.id === col.b);
      if (!A || !B || A.kind === 'diff' || B.kind === 'diff') return { text: '' };
      const a = baseCellOf(A, row, ctx).num; const b = baseCellOf(B, row, ctx).num;
      if (a == null && b == null) return { text: '' };
      const n = (a || 0) - (b || 0);
      return { text: fmtCol(n, col, { blankZero: false }), num: n };
    }

    case 'divide': {
      // A as a percent of B, both read FOR THIS ROW — so a folder divides
      // its totals. A Difference may be either side (Budget Left ÷ Budget);
      // another ratio may not, so there is no loop to chase. Nothing to
      // divide by (B blank or 0) is a blank, not an error or an infinity.
      const cols = ctx.columns || [];
      const A = cols.find((c) => c.id === col.a); const B = cols.find((c) => c.id === col.b);
      if (!A || !B || A.kind === 'divide' || B.kind === 'divide') return { text: '' };
      const a = baseCellOf(A, row, ctx).num; const b = baseCellOf(B, row, ctx).num;
      if (a == null || b == null || b === 0) return { text: '' };
      const pct = (a / b) * 100;
      const dec = col.format && col.format.decimals != null ? col.format.decimals : 1;
      return { text: `${pct.toFixed(dec)}%`, num: pct, pct };
    }

    case 'formula': {
      // Sum: a number per distinct work item under the row, added up (the
      // work-type filter applies). Otherwise the row's own answer, and a
      // folder or a group — which has no work item — shows nothing.
      if (col.agg === 'sum' && !line) {
        const keep = typeTest(col);
        let n = null;
        branch.forEach((k) => {
          const x = itemOf(k);
          if (!x || (keep && !keep(x))) return;
          const v = asNumber(formulaValue(col, x, ctx).value);
          if (v != null) n = (n || 0) + v;
        });
        return { text: fmtCol(n, col), num: n, tone: row.isGroup ? 'agg' : '' };
      }
      // Count: a FLAG and how many carry it. A work item's line shows its own
      // answer ("PROBLEM") and counts as 1, so the column's colour bands paint
      // the flag and the count alike; a folder or a group shows how many
      // distinct work items under it the formula flags (the work-type filter
      // applies). Flagged = any answer but blank, 0, false or an error.
      if (col.agg === 'count') {
        if (rec) {
          const r = formulaValue(col, rec, ctx);
          if (r.error) return { text: '#ERR', title: r.error, tone: 'late' };
          return flagged(r.value) ? { ...formulaCell(r.value, col), num: 1 } : { text: '', num: 0 };
        }
        const keep = typeTest(col);
        let n = 0;
        branch.forEach((k) => {
          const x = itemOf(k);
          if (x && (!keep || keep(x)) && flagged(formulaValue(col, x, ctx).value)) n += 1;
        });
        return { text: n ? String(n) : '', num: n, tone: 'agg' };
      }
      if (!rec) return { text: '' };
      const r = formulaValue(col, rec, ctx);
      if (r.error) return { text: '#ERR', title: r.error, tone: 'late' };
      return formulaCell(r.value, col);
    }

    case 'text':
      if (!rec) return { text: '' };
      return { text: textsOf(rec, fieldId).join(', ') };

    // EXTERNAL DATABASE — this work item's field, looked up in a source's
    // table. The page holds the answers (ctx.lookupOf: undefined while it is
    // still asking, null for no matching row, { value } or { error }); a
    // number keeps the column's format and bands, a date reads as a date.
    case 'lookup': {
      if (!rec) return { text: '' };
      const first = textsOf(rec, fieldId)[0];
      if (first == null || first === '') return { text: '' };
      const look = typeof ctx.lookupOf === 'function' ? ctx.lookupOf(col, first) : null;
      if (look === undefined) return { text: '…', tone: 'muted' };
      if (!look) return { text: '' };
      if (look.error) return { text: '#ERR', title: look.error, tone: 'late' };
      return formulaCell(look.value, col);
    }

    // STOCK CHECK — what is in stock (column A) against what the work needs
    // (column B), netted part by part over every loaded row (./stock.js; the
    // page hands the answer in as ctx.stockOf). A judged row reads "Short 26"
    // or "OK"; every row above reads how many short rows are under it.
    case 'stock': {
      const net = typeof ctx.stockOf === 'function' ? ctx.stockOf(col) : null;
      if (!net) return { text: '' };
      const own = net.byKey.get(row.key) || null;
      let n = own && own.short > 0 && !branch.has(row.key) ? 1 : 0;
      branch.forEach((k) => { n += (net.own.get(k) || 0) + (net.under.get(k) || 0); });
      if (own && own.made && !n) return { text: 'Made', tone: 'muted', num: 0, stock: { own } };
      if (own && !own.made) {
        if (own.error) return { text: '#ERR', title: own.error, tone: 'late', stock: { own } };
        if (own.pending) return { text: '…', tone: 'muted', stock: { own } };
        if (own.short > 0) return { text: `Short ${fmtNum(own.short, { blankZero: false })}`, num: n || 1, band: 'red', stock: { own, n } };
        if (!n) return { text: own.need > 0 ? 'OK' : '', num: 0, band: own.need > 0 ? 'green' : '', stock: { own, n } };
      }
      if (!n) return { text: '', num: 0 };
      return { text: `${n} short`, num: n, band: 'red', tone: 'agg', stock: { own, n } };
    }

    case 'count':
      return { text: row.count ? String(row.count) : '', num: row.count || 0 };

    case 'countSub':
      return { text: row.subRows ? String(row.subRows) : '', num: row.subRows || 0 };

    case 'progress': {
      let done = 0; let total = 0;
      const keep = typeTest(col);
      branch.forEach((k) => { const x = itemOf(k); if (!x || (keep && !keep(x))) return; total += 1; if (isDone(x)) done += 1; });
      if (!total) return { text: '' };
      const pct = Math.round((done / total) * 100);
      return { text: `${pct}%`, num: pct, pct, done, total };
    }

    default:
      return { text: '' };
  }
}

// The columns actually drawn, in order — and the total width of the label
// pane, which the timeline needs to line up with the grid.
export const shownColumns = (cfg) => (cfg.columns || []).filter((c) => c.on !== false);
export const paneWidth = (cols) => cols.reduce((a, c) => a + (c.width || 120), 0);
